import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import MissionControlApi from "../../../../infrastructure/api/missionControlApi";
import { formatMoney } from "../../partner-commission/utils/formatMoney";
import {
  REFRESH_MS,
  pick,
  statusBannerColor,
  statusChipColor,
  severityChipColor,
  statusLabelFa,
  severityLabelFa,
  statSeverity,
  formatPercent,
  funnelConversion,
} from "./missionControlUtils";

const SectionTitle = ({ children, action }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
    <Typography variant="subtitle1" fontWeight={700}>
      {children}
    </Typography>
    {action}
  </Box>
);

const StatCard = ({ title, value, subtitle, severity = "default" }) => {
  const borderColor =
    severity === "error"
      ? "error.light"
      : severity === "warning"
        ? "warning.light"
        : "divider";

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderColor,
        borderWidth: severity !== "default" ? 2 : 1,
      }}
    >
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
          {value ?? "—"}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const HealthCard = ({ label, status, detail }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent sx={{ py: 1.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Box flex={1} minWidth={0}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {label}
          </Typography>
          {detail && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap title={detail}>
              {detail}
            </Typography>
          )}
        </Box>
        <Chip
          size="small"
          color={statusChipColor(status)}
          label={statusLabelFa(status)}
          sx={{ flexShrink: 0 }}
        />
      </Box>
    </CardContent>
  </Card>
);

const FunnelTable = ({ funnel }) => {
  const periods = [
    { key: "today", label: "امروز", data: pick(funnel, "today", "Today", {}) },
    { key: "last7Days", label: "۷ روز", data: pick(funnel, "last7Days", "Last7Days", {}) },
    { key: "currentMonth", label: "ماه جاری", data: pick(funnel, "currentMonth", "CurrentMonth", {}) },
  ];
  const stages = [
    ["visits", "Visits", "بازدید"],
    ["registrations", "Registrations", "ثبت‌نام"],
    ["projectRequests", "ProjectRequests", "درخواست پروژه"],
    ["consultations", "Consultations", "مشاوره"],
    ["contracts", "Contracts", "قرارداد"],
    ["installations", "Installations", "نصب"],
    ["gridConnections", "GridConnections", "اتصال به شبکه"],
  ];

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>مرحله</TableCell>
          {periods.map((p) => (
            <TableCell key={p.key} align="right">
              {p.label}
            </TableCell>
          ))}
          <TableCell align="right">تبدیل (امروز)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {stages.map(([camel, pascal, label], idx) => {
          const todayData = periods[0].data;
          const prevCamel = idx > 0 ? stages[idx - 1][0] : null;
          const prevPascal = idx > 0 ? stages[idx - 1][1] : null;
          const conversion =
            idx === 0
              ? "—"
              : funnelConversion(
                  pick(todayData, prevCamel, prevPascal, 0),
                  pick(todayData, camel, pascal, 0)
                );

          return (
            <TableRow key={camel}>
              <TableCell>{label}</TableCell>
              {periods.map((p) => (
                <TableCell key={`${p.key}-${camel}`} align="right">
                  {pick(p.data, camel, pascal, "—")}
                </TableCell>
              ))}
              <TableCell align="right">{conversion}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const MissionControlDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await MissionControlApi.getDashboard();
      const normalized = MissionControlApi.normalize(
        res,
        "بارگذاری مرکز فرمان ناموفق بود."
      );
      if (normalized.error) {
        setError(normalized.error);
        setData(null);
      } else {
        setData(normalized.data);
      }
      setLastRefresh(new Date());
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری داشبورد");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const overallStatus = pick(data, "overallStatus", "OverallStatus", "Unknown");
  const overallMessage = pick(data, "overallMessage", "OverallMessage", "");
  const executive = pick(data, "executiveSummary", "ExecutiveSummary", {});
  const health = pick(data, "health", "Health", {});
  const healthComponents = pick(health, "components", "Components", []);
  const metrics = pick(data, "systemMetrics", "SystemMetrics", {});
  const security = pick(data, "security", "Security", {});
  const userActivity = pick(data, "userActivity", "UserActivity", {});
  const marketGrowth = pick(data, "marketGrowth", "MarketGrowth", {});
  const finance = pick(data, "finance", "Finance", {});
  const funnel = pick(data, "funnel", "Funnel", {});
  const alerts = useMemo(
    () => pick(data, "alerts", "Alerts", []) || [],
    [data]
  );
  const capacity = pick(data, "capacity", "Capacity", {});

  const serverErrorRate = pick(metrics, "errorRatePercent", "ErrorRatePercent", 0);
  const serverErrorsPm = pick(metrics, "serverErrorsPerMinute", "ServerErrorsPerMinute", 0);
  const clientErrorsPm = pick(metrics, "clientErrorsPerMinute", "ClientErrorsPerMinute", 0);
  const otpSuccessRate = pick(metrics, "otpSuccessRatePercent", "OtpSuccessRatePercent", null);

  const StatusIcon =
    overallStatus === "Healthy"
      ? CheckCircleIcon
      : overallStatus === "Warning"
        ? WarningAmberIcon
        : ErrorIcon;

  const defaultHealthCards = [
    ["API", "api", pick(health, "apiStatus", "ApiStatus"), null],
    ["پایگاه داده", "database", pick(health, "databaseStatus", "DatabaseStatus"),
      pick(health, "databaseLatencyMs", "DatabaseLatencyMs") != null
        ? `${pick(health, "databaseLatencyMs", "DatabaseLatencyMs")}ms`
        : null],
    ["Redis", "redis", pick(health, "redisStatus", "RedisStatus"), null],
    ["پیامک", "sms", pick(health, "smsStatus", "SmsStatus"), null],
    ["ذخیره‌سازی", "storage", pick(health, "storageStatus", "StorageStatus"), null],
    ["دیسک", "disk", pick(health, "diskStatus", "DiskStatus"),
      pick(health, "diskFreePercent", "DiskFreePercent") != null
        ? `${pick(health, "diskFreePercent", "DiskFreePercent").toLocaleString("fa-IR", { maximumFractionDigits: 1 })}٪ آزاد`
        : null],
    ["پشتیبان", "backup", pick(health, "backupStatus", "BackupStatus"), null],
  ];

  const healthCards = healthComponents.length > 0
    ? healthComponents.map((c) => [
        pick(c, "label", "Label"),
        pick(c, "key", "Key"),
        pick(c, "status", "Status"),
        pick(c, "detail", "Detail"),
      ])
    : defaultHealthCards;

  const attentionItems = pick(executive, "attentionItems", "AttentionItems", []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            مرکز فرمان سولارنت
          </Typography>
          <Typography variant="body2" color="text.secondary">
            نمای یکپارچه عملیات — زیرساخت، امنیت، کسب‌وکار و مالی — بروزرسانی هر ۳۰ ثانیه
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            component={RouterLink}
            to="/admin/system-health"
            variant="text"
            endIcon={<OpenInNewIcon fontSize="small" />}
          >
            سلامت سیستم
          </Button>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            بروزرسانی
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={load}>تلاش مجدد</Button>}>
          {error}
        </Alert>
      )}

      {!data && !loading && !error && (
        <Alert severity="info">داده‌ای برای نمایش وجود ندارد.</Alert>
      )}

      {data && (
        <>
          <Card
            sx={{
              mb: 2,
              bgcolor: statusBannerColor(overallStatus),
              color: "common.white",
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <StatusIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="overline" sx={{ opacity: 0.9 }}>
                      وضعیت کلی سامانه — {statusLabelFa(overallStatus)}
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {overallMessage || overallStatus}
                    </Typography>
                    {lastRefresh && (
                      <Typography variant="caption" sx={{ opacity: 0.85 }}>
                        آخرین بروزرسانی: {lastRefresh.toLocaleTimeString("fa-IR")}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={`${pick(executive, "criticalAlertCount", "CriticalAlertCount", 0)} بحرانی`}
                    sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "inherit" }}
                    size="small"
                  />
                  <Chip
                    label={`${pick(executive, "warningAlertCount", "WarningAlertCount", 0)} هشدار`}
                    sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "inherit" }}
                    size="small"
                  />
                  <Chip
                    label={`${pick(executive, "healthyComponents", "HealthyComponents", 0)}/${pick(executive, "totalComponents", "TotalComponents", 0)} سرویس سالم`}
                    sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "inherit" }}
                    size="small"
                  />
                </Stack>
              </Box>
            </CardContent>
          </Card>

          {attentionItems.length > 0 && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                  نیازمند توجه فوری
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {attentionItems.map((item, idx) => (
                    <Chip
                      key={`att-${idx}`}
                      icon={<WarningAmberIcon />}
                      label={item}
                      color={overallStatus === "Critical" ? "error" : "warning"}
                      variant="outlined"
                      sx={{ maxWidth: "100%", height: "auto", "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 } }}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          <SectionTitle
            action={
              alerts.length > 0 ? (
                <Chip
                  size="small"
                  color={alerts.some((a) => (pick(a, "severity", "Severity") || "").toLowerCase() === "critical") ? "error" : "warning"}
                  label={`${alerts.length} هشدار فعال`}
                />
              ) : null
            }
          >
            هشدارهای عملیاتی
          </SectionTitle>
          {alerts.length === 0 ? (
            <Alert severity="success" icon={<MonitorHeartIcon />} sx={{ mb: 3 }}>
              هشدار فعالی ثبت نشده است.
            </Alert>
          ) : (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={100}>شدت</TableCell>
                    <TableCell width={180}>عنوان</TableCell>
                    <TableCell>توضیح</TableCell>
                    <TableCell width={90}>اقدام</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alerts.map((alert, idx) => (
                    <TableRow key={`${pick(alert, "title", "Title")}-${idx}`}>
                      <TableCell>
                        <Chip
                          size="small"
                          color={severityChipColor(pick(alert, "severity", "Severity"))}
                          label={severityLabelFa(pick(alert, "severity", "Severity"))}
                        />
                      </TableCell>
                      <TableCell>{pick(alert, "title", "Title")}</TableCell>
                      <TableCell>{pick(alert, "description", "Description")}</TableCell>
                      <TableCell>
                        {pick(alert, "actionUrl", "ActionUrl") ? (
                          <MuiLink component={RouterLink} to={pick(alert, "actionUrl", "ActionUrl")}>
                            مشاهده
                          </MuiLink>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}

          <SectionTitle>سلامت زیرساخت</SectionTitle>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {healthCards.map(([label, key, status, detail]) => (
              <Grid item xs={6} sm={4} md={3} lg={12 / 7} key={key || label}>
                <HealthCard label={label} status={status} detail={detail} />
              </Grid>
            ))}
          </Grid>

          <SectionTitle>ضربان لحظه‌ای سیستم</SectionTitle>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="درخواست/دقیقه" value={pick(metrics, "requestsPerMinute", "RequestsPerMinute", 0)} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                title="نرخ خطای 5xx"
                value={formatPercent(serverErrorRate, "۰٪")}
                subtitle={`${serverErrorsPm} خطا در دقیقه جاری`}
                severity={statSeverity(serverErrorRate, { warn: 1, critical: 5 })}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                title="خطای 4xx (دقیقه)"
                value={clientErrorsPm}
                subtitle="احراز هویت / دسترسی"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="کاربران فعال (۱۵د)" value={pick(metrics, "activeUsers15Min", "ActiveUsers15Min", 0)} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                title="موفقیت OTP"
                value={otpSuccessRate != null ? formatPercent(otpSuccessRate) : "—"}
                subtitle={`${pick(metrics, "otpSent", "OtpSent", 0)}/${pick(metrics, "otpRequests", "OtpRequests", 0)} ارسال`}
                severity={
                  otpSuccessRate != null
                    ? statSeverity(100 - otpSuccessRate, { warn: 20, critical: 50 })
                    : "default"
                }
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                title="خطای SMS"
                value={pick(metrics, "smsFailed", "SmsFailed", 0)}
                severity={statSeverity(pick(metrics, "smsFailed", "SmsFailed", 0), { warn: 5, critical: 20 })}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <SectionTitle>فعالیت کاربران</SectionTitle>
                  <Grid container spacing={1.5}>
                    {[
                      ["کاربران امروز", pick(userActivity, "usersToday", "UsersToday", 0)],
                      ["ثبت‌نام امروز", pick(userActivity, "registrationsToday", "RegistrationsToday", 0)],
                      ["ورود امروز", pick(userActivity, "loginsToday", "LoginsToday", 0)],
                      ["کل مشتریان", pick(userActivity, "totalCustomers", "TotalCustomers", 0)],
                      ["کل نمایندگان", pick(userActivity, "totalPartners", "TotalPartners", 0)],
                      ["کل کاربران", pick(userActivity, "totalUsers", "TotalUsers", 0)],
                    ].map(([title, value]) => (
                      <Grid item xs={6} key={title}>
                        <StatCard title={title} value={value} />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <SectionTitle>امنیت و احراز هویت</SectionTitle>
                  <Grid container spacing={1.5}>
                    {[
                      ["ورود ناموفق", pick(security, "loginFailures", "LoginFailures", 0), { warn: 10, critical: 50 }],
                      ["محدودیت ورود", pick(security, "loginRateLimited", "LoginRateLimited", 0), { warn: 5, critical: 20 }],
                      ["OTP ناموفق", pick(security, "otpFailed", "OtpFailed", 0), { warn: 5, critical: 20 }],
                      ["محدودیت OTP", pick(security, "otpRateLimited", "OtpRateLimited", 0), { warn: 5, critical: 20 }],
                      ["SMS ناموفق", pick(security, "smsFailures", "SmsFailures", 0), { warn: 5, critical: 20 }],
                      ["تلاش ورود امروز", pick(metrics, "loginAttempts", "LoginAttempts", 0), null],
                    ].map(([title, value, thresholds]) => (
                      <Grid item xs={6} key={title}>
                        <StatCard
                          title={title}
                          value={value}
                          severity={thresholds ? statSeverity(value, thresholds) : "default"}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <SectionTitle>ظرفیت و آمادگی</SectionTitle>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">حالت ذخیره‌سازی</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {pick(capacity, "storageMode", "StorageMode", "—")}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Redis</Typography>
                      <Chip
                        size="small"
                        color={pick(capacity, "redisEnabled", "RedisEnabled") ? "success" : "default"}
                        label={pick(capacity, "redisEnabled", "RedisEnabled") ? "فعال" : "غیرفعال"}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">معماری</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {pick(capacity, "multiNodeReady", "MultiNodeReady") ? "چندنود" : "تک‌نود"}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">کاربر همزمان امن</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {pick(capacity, "safeConcurrentUsersEstimate", "SafeConcurrentUsersEstimate", 50)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">نود API</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {pick(capacity, "apiNodeCount", "ApiNodeCount", 1)}
                      </Typography>
                    </Box>
                    <Divider />
                    <Typography variant="caption" color="text.secondary">
                      {pick(capacity, "lastLoadTestSummary", "LastLoadTestSummary", "")}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} lg={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <SectionTitle>توسعه بازار</SectionTitle>
                  <Grid container spacing={1.5}>
                    {[
                      ["نمایندگان فعال", pick(marketGrowth, "activePartners", "ActivePartners", 0)],
                      ["بازدید ارجاع امروز", pick(marketGrowth, "referralVisitsToday", "ReferralVisitsToday", 0)],
                      ["ثبت‌نام ارجاعی", pick(marketGrowth, "referredRegistrationsToday", "ReferredRegistrationsToday", 0)],
                      ["درخواست ارجاعی", pick(marketGrowth, "referredProjectRequestsToday", "ReferredProjectRequestsToday", 0)],
                      ["پورسانت امروز", formatMoney(pick(marketGrowth, "commissionsTodayAmount", "CommissionsTodayAmount", 0))],
                      ["پورسانت پرداخت‌شده", formatMoney(pick(marketGrowth, "paidCommissionsAmount", "PaidCommissionsAmount", 0))],
                      ["پورسانت پرداخت‌نشده", formatMoney(pick(marketGrowth, "unpaidCommissionsAmount", "UnpaidCommissionsAmount", 0))],
                      ["تسویه معلق (مبلغ)", formatMoney(pick(marketGrowth, "pendingPayoutsAmount", "PendingPayoutsAmount", 0))],
                      ["هشدار تقلب باز", pick(marketGrowth, "openFraudAlerts", "OpenFraudAlerts", 0), { warn: 1, critical: 5 }],
                    ].map(([title, value, thresholds]) => (
                      <Grid item xs={6} sm={4} key={title}>
                        <StatCard
                          title={title}
                          value={value}
                          severity={thresholds ? statSeverity(Number(value) || 0, thresholds) : "default"}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <SectionTitle>مالی</SectionTitle>
                  <Grid container spacing={1.5}>
                    {[
                      ["پرداخت موفق امروز", pick(finance, "successfulPaymentsToday", "SuccessfulPaymentsToday", 0)],
                      ["پرداخت ناموفق امروز", pick(finance, "failedPaymentsToday", "FailedPaymentsToday", 0)],
                      ["مبلغ پرداخت امروز", formatMoney(pick(finance, "paymentAmountToday", "PaymentAmountToday", 0))],
                      ["تسویه معلق (تعداد)", pick(finance, "pendingPartnerPayouts", "PendingPartnerPayouts", 0)],
                      ["تسویه پرداخت‌شده امروز", formatMoney(pick(finance, "paidPartnerPayoutsToday", "PaidPartnerPayoutsToday", 0))],
                      ["پورسانت پرداخت‌نشده", formatMoney(pick(finance, "unpaidCommissionAmount", "UnpaidCommissionAmount", 0))],
                    ].map(([title, value]) => (
                      <Grid item xs={6} sm={4} key={title}>
                        <StatCard title={title} value={value} />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <SectionTitle>قیف تبدیل</SectionTitle>
              <FunnelTable funnel={funnel} />
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default MissionControlDashboardPage;
